import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import {
  Alert,
  Box,
  Dialog,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';

import { ForgotPasswordForm } from '../ForgotPasswordForm';
import { LoginForm } from '../LoginForm';
import { SignUpForm, SignUpPrefill } from '../SignUpForm';
import { authModalAtom } from '../../atoms';
import { getGuestVideos, getInviteShown, saveInviteShown } from '../../helpers';
import { Context, StateAuth, useAuth } from '../../context/auth';
import { authBody } from '../../theme';
import InviteContent, { InviteVariant } from './InviteContent';
import styles from './style';

// Reasons that have no text of their own in the sign up view.
const GENERIC_REASONS = ['login', 'signup', 'signupInvite'];

// Login popup of a visitor: the real login, sign up and forgot password forms, with a title that tells why it opened.
// Same size as the card of the auth pages. The feed stays behind: after the login the user comes back to the same video.
const AuthModal: React.FC = React.memo(() => {
  const { t } = useTranslation();
  const { user } = useAuth() as Context;
  const [authModal, setAuthModal] = useAtom(authModalAtom);
  // The last opened popup: while it fades out, its title, text and form stay as they were (nothing unmounts first).
  const [shown, setShown] = useState(authModal);
  if (authModal && authModal !== shown) {
    setShown(authModal);
  }
  const [prefill, setPrefill] = useState<SignUpPrefill | undefined>(undefined);
  // Forgot password view: local to the popup, on top of its login view.
  const [isForgot, setIsForgot] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [inviteVariant, setInviteVariant] = useState<InviteVariant>('clips');

  // The invitations alternate between the two variants.
  const isInvite = !!authModal && authModal.reason === 'signupInvite' && authModal.view === 'signupInvite';
  useEffect(() => {
    if (isInvite) {
      setInviteVariant(getInviteShown() % 2 === 0 ? 'clips' : 'count');
      saveInviteShown();
    }
  }, [isInvite]);

  const close = useCallback(() => {
    setAuthModal(null);
  }, [setAuthModal]);

  // Each opening starts on the view asked, never on the forgot password form left open.
  // Reset once the popup is gone: during the fade out the view that was open stays (no flash of the login).
  const onExited = useCallback(() => {
    setIsForgot(false);
    setPrefill(undefined);
  }, []);

  // Logged in or signed up: the popup has done its job.
  useEffect(() => {
    if (user.isConnected === StateAuth.CONNECTED && authModal) {
      close();
    }
  }, [authModal, close, user.isConnected]);

  const toLogin = useCallback(() => {
    setPrefill(undefined);
    setIsForgot(false);
    setAuthModal((prev) => (prev ? { ...prev, view: 'login' } : prev));
  }, [setAuthModal]);

  const toSignup = useCallback(() => {
    setIsForgot(false);
    setAuthModal((prev) => (prev ? { ...prev, view: 'signup' } : prev));
  }, [setAuthModal]);

  const toForgot = useCallback(() => {
    setIsForgot(true);
  }, []);

  // New password saved: back to the login view, with the message of the page.
  const onPasswordReset = useCallback(() => {
    toLogin();
    setIsPasswordReset(true);
  }, [toLogin]);

  const onCloseResetSnack = useCallback(() => {
    setIsPasswordReset(false);
  }, []);

  // A Google, Apple or Facebook account without SoSkills account: its sign up, filled.
  const onSocialSignUp = useCallback((data: SignUpPrefill) => {
    setPrefill(data);
    setAuthModal((prev) => (prev ? { ...prev, view: 'signup' } : prev));
  }, [setAuthModal]);

  const texts = useMemo(() => {
    if (!shown) {
      return { title: '', text: '' };
    }
    // The invitation shows its own title and text.
    if (shown.view === 'signupInvite') {
      return { title: '', text: '' };
    }
    if (isForgot) {
      // The form shows its own subtitle: it changes with the steps.
      return { title: t('forgotPassword.title'), text: '' };
    }
    const { reason, view, username } = shown;
    const values = { username: username || '' };
    if (view === 'signup') {
      const textReason = GENERIC_REASONS.includes(reason) ? 'signup' : reason;
      return {
        title: t('admin.guest.reasons.signup.title'),
        text: t(`admin.guest.reasons.${textReason}.text`, values),
      };
    }
    if (view === 'login' && reason === 'signupInvite') {
      return {
        title: t('admin.guest.reasons.login.title'),
        text: t('admin.guest.reasons.login.text'),
      };
    }
    return {
      title: t(`admin.guest.reasons.${reason}.title`, values),
      text: t(`admin.guest.reasons.${reason}.text`, values),
    };
  }, [isForgot, shown, t]);

  const transitionProps = useMemo(() => ({ onExited }), [onExited]);

  const view = useMemo(() => {
    if (isForgot) {
      return 'forgot';
    }
    return shown ? shown.view : 'login';
  }, [isForgot, shown]);

  return (
    <Dialog open={!!authModal} onClose={close} TransitionProps={transitionProps} PaperProps={styles.paperProps} sx={styles.dialog}>
      <IconButton onClick={close} aria-label={t('admin.common.close')} sx={styles.close}>
        <Close fontSize="small" />
      </IconButton>
      <Box sx={authBody}>
        {!!texts.title && <Typography variant="h4" component="h2" align="center" sx={styles.title}>{texts.title}</Typography>}
        {!!texts.text && <Typography sx={styles.text}>{texts.text}</Typography>}
        {view === 'signupInvite' && (
          <InviteContent
            variant={inviteVariant}
            videos={getGuestVideos()}
            onSignUp={toSignup}
            onLogin={toLogin}
            onLater={close}
          />
        )}
        {view === 'login' && (
          <LoginForm
            embedded
            onSwitchToSignup={toSignup}
            onForgotPassword={toForgot}
            onSocialSignUp={onSocialSignUp}
          />
        )}
        {view === 'signup' && (
          <SignUpForm embedded onSwitchToLogin={toLogin} prefill={prefill} />
        )}
        {view === 'forgot' && (
          <ForgotPasswordForm embedded onBackToLogin={toLogin} onDone={onPasswordReset} />
        )}
      </Box>
      <Snackbar
        open={isPasswordReset}
        autoHideDuration={3000}
        onClose={onCloseResetSnack}
        anchorOrigin={styles.toastOrigin}
        sx={styles.toast}
      >
        <Alert severity="success" variant="filled" onClose={onCloseResetSnack} sx={styles.toastAlert}>
          {t('form.passwordSent')}
        </Alert>
      </Snackbar>
    </Dialog>
  );
});

export default AuthModal;
